import React, { Suspense, lazy, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { getDef } from "./defsCache";
import { titleOf } from "./staffUtils";
import "./staff.css";

// The patient renderer is a lazy chunk (with forms.css): the care bundle only
// gains this file.
const FormRenderer = lazy(() => import("../renderer/FormRenderer"));

const noopSubmit = () => Promise.resolve();

/*
 * Side panel preview of one form exactly as the patient will see it, with the
 * renderer's own EN/ES toggle. Nothing is stored (no storage adapter) and
 * submitting does nothing.
 *   form    {id, version, title} from the catalog, or null (closed)
 *   prefill {patient, appointment, practice} as the patient page would get it
 */
// audience="staff": the staff-mode preview of an internal staff form.
export default function FormPreviewDialog({ form, onClose, prefill, practiceName, initialLang = "en", audience = "patient" }) {
  const [def, setDef] = useState(null);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(initialLang);
  const formKey = form ? `${form.id}@${form.version}` : "";

  useEffect(() => {
    let alive = true;
    setDef(null);
    setError("");
    if (!form) return undefined;
    getDef(form.id, form.version)
      .then((d) => {
        if (alive) setDef(d);
      })
      .catch(() => {
        if (alive) setError("Couldn't load this form. Try again.");
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  useEffect(() => {
    setLang(initialLang);
  }, [initialLang, formKey]);

  return (
    <Dialog
      open={!!form}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-label={form ? `Preview: ${titleOf(form.title)}` : "Preview"}
      PaperProps={{
        sx: {
          position: "fixed",
          right: 0,
          top: 0,
          m: 0,
          height: "100%",
          maxHeight: "100%",
          width: { xs: "100%", sm: 600 },
          maxWidth: "100%",
          borderRadius: 0,
        },
      }}
    >
      <div className="fws fws-side" data-testid="fws-preview">
        <div className="fws-side-head">
          <div className="fws-banner" role="status">Preview — nothing is saved</div>
          <button type="button" className="fws-icon-btn" aria-label="Close preview" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="fws-side-body">
          {error && <div className="fws-alert fws-alert-error" style={{ margin: 16 }}>{error}</div>}
          {!def && !error && <div className="fws-muted" style={{ padding: 16 }}>Loading the form…</div>}
          {def && (
            <Suspense fallback={<div className="fws-muted" style={{ padding: 16 }}>Loading the form…</div>}>
              <FormRenderer
                key={formKey}
                definition={def}
                audience={audience}
                lang={lang}
                onLangChange={setLang}
                prefill={prefill || {}}
                practiceName={practiceName}
                onSubmit={noopSubmit}
                stickyNav={false}
              />
            </Suspense>
          )}
        </div>
      </div>
    </Dialog>
  );
}
